import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  NativeDateAdapter,
} from '@angular/material/core';
import {
  MatDatepicker,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { CommunityAdditionalDataField } from '@models/communities/community-additional-data-field.model';
import { QuestionType } from '@models/event-attendance/event-attendee-question-type.enum';
import { EventAttendeeQuestion } from '@models/event-attendance/event-attendee-question.model';
import { EventAttendee } from '@models/event-attendance/event-attendee.model';
import { AccountRequirement } from '@models/events/account-requirement.enum';
import { EventSlot } from '@models/events/event-slot.model';
import { TranslateModule } from '@ngx-translate/core';
import { truncateHtml } from '@utils/html-truncate.util';
import { FormsModules } from '@utils/shared-modules';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { TextEditableDirective } from '../../../../directives/text-editable.directive';

class CustomDateAdapter extends NativeDateAdapter {
  override format(date: Date): string {
    return `${date.getFullYear()}`;
  }
}

@Component({
  selector: 'app-event-rsvp-question',
  standalone: true,
  imports: [
    CommonModule,
    FormsModules,
    MatCheckboxModule,
    MatDatepickerModule,
    TranslateModule,
    TextEditableDirective,
    NgxMaskDirective,
  ],
  providers: [
    provideNgxMask(),
    { provide: DateAdapter, useClass: CustomDateAdapter },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: { dateInput: 'YYYY' },
        display: {
          dateInput: 'YYYY',
          monthYearLabel: 'YYYY',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: 'YYYY',
        },
      },
    },
  ],
  templateUrl: './event-rsvp-question.component.html',
  styleUrl: './event-rsvp-question.component.scss',
})
export class EventRsvpQuestionComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() form?: FormGroup;
  @Input() question?: EventAttendeeQuestion | CommunityAdditionalDataField;
  @Input() ngModelVal: any;
  @Input() disabled: boolean = false;
  @Input() editDisabled: boolean = false;
  @Input() isLast: boolean = false;
  @Input() slot?: EventSlot;
  @Input() defaultValue?: any = '';
  @Input() matFormFieldClass = 'flex-input';
  @Input() matFormFieldAppearance: MatFormFieldAppearance = 'outline';
  @Input() isToggle = true;
  @Input() attendee?: EventAttendee;

  @Output() ngModelValChange = new EventEmitter<any>();
  @Output() onChangeValue = new EventEmitter<any>();

  /** Subject that emits when the component has been destroyed. */
  private _onDestroy = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.form && this.question) {
      const controlName = `question#${this.question.id}`;
      const controlExists = this.form.get(controlName);

      if (!controlExists) {
        this.addQuestionControl();
      } else {
        this.updateQuestionControl();
      }
    }
  }

  ngOnInit(): void {
    if (this.form && this.question) {
      this.addQuestionControl();
    }
  }

  getAttendeeAnswer(): any {
    if (!this.attendee || !this.attendee.additionalData || !this.question)
      return;

    const answerDataField = this.attendee.additionalData.find(
      (x) => x.dataFieldId === this.question?.id,
    );
    if (!answerDataField) return;

    if (this.question.type === QuestionType.MULTISELECT_DROPDOWN) {
      const answers = this.attendee.additionalData.filter(
        (answer) =>
          answer.dataFieldId === this.question?.id && answer.dataFieldOptionId,
      );

      if (!answers || answers.length === 0) {
        return undefined;
      }

      return answers.map((answer) => answer.dataFieldOptionId!);
    }

    let answer: any = '';

    if (answerDataField.dataFieldOptionId) {
      answer = answerDataField.dataFieldOptionId;
    } else if (answerDataField.boolAnswer) {
      answer = answerDataField.boolAnswer;
    } else {
      answer = answerDataField.openAnswer;
    }

    return answer;
  }

  onChange(controlName: any) {
    if (this.form?.controls[controlName]) {
      const value = this.form.controls[controlName].value;
      this.onChangeValue.emit(value);
    }
  }

  MAX_LABEL_LENGTH = 128;

  expandedStates: { [id: string]: boolean } = {};
  getAsteriskIfRequired(
    question: EventAttendeeQuestion | CommunityAdditionalDataField,
  ): string {
    if (question.isRequired) {
      if (
        this.slot &&
        this.slot.accountRequirement === AccountRequirement.REQUIRED
      ) {
        return '';
      }
      return '*';
    }
    return '';
  }

  toggleExpanded(id: number): void {
    this.expandedStates[id] = !this.expandedStates[id];
  }

  isExpanded(id: number): boolean {
    return this.expandedStates[id] || false;
  }

  getTrimmedLabel(label: string): string {
    if (!this.isLongLabel(label)) return label;
    return truncateHtml(label, this.MAX_LABEL_LENGTH, { ellipsis: '' }); // we add "..." manually in template
  }

  getVisibleText(html: string): string {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent || '';
  }

  isLongLabel(label: string): boolean {
    return this.getVisibleText(label).length > this.MAX_LABEL_LENGTH;
  }

  updateQuestionControl() {
    if (this.form && this.question) {
      const controlName = `question#${this.question.id}`;
      const control = this.form.get(controlName);

      if (control) {
        let baseValue =
          this.ngModelVal ||
          (this.question.type === QuestionType.MULTISELECT_DROPDOWN
            ? []
            : this.question.type === QuestionType.CHECKBOX
              ? false
              : '');

        const answer = this.getAttendeeAnswer();
        if (answer) baseValue = answer;
        control.setValue(baseValue);
      }
    }
  }

  addQuestionControl() {
    if (this.question && this.form) {
      const controlName = `question#${this.question.id}`;

      let baseValue =
        this.ngModelVal ||
        (this.question.type === QuestionType.MULTISELECT_DROPDOWN
          ? []
          : this.question.type === QuestionType.CHECKBOX
            ? false
            : '');

      const answer = this.getAttendeeAnswer();
      const validators = [];

      if (this.question.isRequired) {
        if (this.question.type === QuestionType.CHECKBOX) {
          validators.push(Validators.requiredTrue);
        } else {
          validators.push(Validators.required);
        }
      }

      if (this.question.type === QuestionType.TIME_DURATION) {
        validators.push(Validators.pattern(/^(\d\d):(\d\d):(\d\d)$/));
      }

      if (answer) baseValue = answer;
      const control = this.fb.control(baseValue, validators);

      if (this.disabled) {
        control.disable();
      }

      this.form.addControl(controlName, control);

      this.form
        .get(controlName)
        ?.valueChanges.pipe(
          takeUntil(this._onDestroy),
          debounceTime(250),
          distinctUntilChanged(),
        )
        .subscribe((value) => {
          this.onChangeValue.emit(value);
        });

      if (this.defaultValue) {
        this.form.controls[controlName].patchValue(this.defaultValue);
      }
    }
  }

  onNgModelChange(val: any): void {
    if (this.ngModelVal === val) {
      return;
    }
    this.ngModelVal = val;
    if (this.question?.type === QuestionType.TIME_DURATION) {
      const isValidTime = /^(\d\d):(\d\d):(\d\d)$/.test(val);
      if (!isValidTime && val !== '') {
        return;
      }
    }
    this.ngModelValChange.emit(val);
  }

  isMultiselectDropdownOptionSelected(optionId?: number): boolean {
    if (!this.question) {
      return false;
    }

    if (this.form) {
      const controlName = `question#${this.question.id}`;
      const control = this.form.get(controlName);

      if (control) {
        const value = control.value;

        if (Array.isArray(value)) {
          return value.includes(optionId);
        }

        return false;
      }
    }

    return Array.isArray(this.ngModelVal)
      ? this.ngModelVal.includes(optionId)
      : false;
  }

  chosenYearHandler(date: Date, yearPicker: MatDatepicker<Date>) {
    if (!this.question) {
      return;
    }

    const normalizedYear = date.getFullYear();
    if (this.form) {
      const controlName = `question#${this.question.id}`;

      const control = this.form.get(controlName);

      if (control) {
        control.setValue(normalizedYear);
      }
    }

    yearPicker.close();
  }

  get displayYear(): Date | null {
    if (!this.question) {
      return null;
    }
    if (this.form) {
      const controlName = `question#${this.question.id}`;

      const control = this.form.get(controlName);

      if (control) {
        const year = control.value;
        return year ? new Date(year, 0, 1) : null;
      }
    }

    return null;
  }

  get minDate(): Date | null {
    if (!this.question || !this.question.min) {
      return null;
    }
    return new Date(this.question.min, 0, 1); // January 1st of minYear
  }

  get maxDate(): Date | null {
    if (!this.question || !this.question.max) {
      return null;
    }
    return new Date(this.question.max, 11, 31); // December 31st of maxYear
  }

  noop() {}

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  protected readonly EventAttendeeQuestionType = QuestionType;
}
